import type { MembershipDocument, WorkspaceMembershipRole } from '@myplayer/schema';
import { IconBrandShield, IconPlus } from '@myplayer/ui';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  LinearSpinner,
  Spinner,
  Tag,
  ToastNotification,
} from './components/ui/primitives.js';
import {
  callActivateInvitedMembershipFunction,
  callManageWorkspaceMembershipFunction,
  getFirebaseClients,
  type ManageWorkspaceMembershipCallablePayload,
  type ManageWorkspaceMembershipCallableResult,
} from './firebase.js';
import {
  EditMemberRoleModal,
  InviteMemberModal,
  MemberRow,
  isSelfMember,
  mapSnapshotToMemberships,
  resolveDisplayedMemberRole,
} from './member-row.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';

export { ASSIGNABLE_MEMBER_ROLES } from './member-row.js';

export interface MembersSectionProps {
  readonly initialMemberships?: readonly MembershipDocument[];
  readonly onManageMembership?: (
    payload: ManageWorkspaceMembershipCallablePayload,
  ) => Promise<ManageWorkspaceMembershipCallableResult>;
}

export function MembersSection(props: MembersSectionProps): ReactElement {
  const { session, can, availableWorkspaces } = useAuth();
  const canManageMembers = can('membership:manage_callable');
  const canReadMembers = can('membership:read');

  const isControlled = props.initialMemberships !== undefined;
  const [memberships, setMemberships] = useState<readonly MembershipDocument[]>(
    props.initialMemberships ?? [],
  );
  const [loading, setLoading] = useState<boolean>(!isControlled);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceMembershipRole>('CAMPAIGN_EDITOR');
  const [inviteWorkspaceId, setInviteWorkspaceId] = useState<string>(
    session?.activeWorkspaceId ?? '',
  );
  const [editingMember, setEditingMember] = useState<MembershipDocument | null>(null);
  const [editingRoleDraft, setEditingRoleDraft] =
    useState<WorkspaceMembershipRole>('CAMPAIGN_EDITOR');
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string>(
    session?.activeWorkspaceId ?? '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (session?.activeWorkspaceId) {
      setInviteWorkspaceId(session.activeWorkspaceId);
      setEditingWorkspaceId(session.activeWorkspaceId);
    }
  }, [session?.activeWorkspaceId]);

  useEffect(() => {
    if (isControlled || !session || !canReadMembers) {
      if (isControlled) {
        const initialList = props.initialMemberships ?? [];
        setMemberships(
          initialList.map((m) =>
            isSelfMember(m, session) && m.status === 'INVITED' ? { ...m, status: 'ACTIVE' } : m,
          ),
        );
      }
      setLoading(false);
      return;
    }

    const { db } = getFirebaseClients();
    return onSnapshot(
      collection(db, 'organisations', session.orgId, 'memberships'),
      (snap) => {
        const loaded = mapSnapshotToMemberships(snap.docs, session.orgId);
        if (loaded.some((m) => isSelfMember(m, session) && m.status === 'INVITED')) {
          void callActivateInvitedMembershipFunction({ orgId: session.orgId }).catch(() => {});
        }
        setMemberships(
          loaded.map((m) =>
            isSelfMember(m, session) && m.status === 'INVITED' ? { ...m, status: 'ACTIVE' } : m,
          ),
        );
        setLoading(false);
      },
      (err) => {
        setErrorMessage(err.message);
        setLoading(false);
      },
    );
  }, [isControlled, props.initialMemberships, session, canReadMembers]);

  const invokeManage =
    props.onManageMembership ??
    ((payload: ManageWorkspaceMembershipCallablePayload) =>
      callManageWorkspaceMembershipFunction(payload));

  const applyLocalOptimisticUpdate = (
    payload: ManageWorkspaceMembershipCallablePayload,
    result: ManageWorkspaceMembershipCallableResult,
  ): void => {
    if (!isControlled || !session) return;
    setMemberships((prev) => {
      const normalizedEmail = payload.email.trim().toLowerCase();
      const idx = prev.findIndex((m) => m.email.toLowerCase() === normalizedEmail);
      const updatedItem: MembershipDocument = {
        membershipId: result.membershipId,
        uid: idx >= 0 ? (prev[idx]?.uid ?? result.membershipId) : result.membershipId,
        email: normalizedEmail,
        orgId: payload.orgId,
        orgRole: payload.action === 'REVOKE' ? null : (payload.orgRole ?? null),
        workspaceRoles: payload.action === 'REVOKE' ? {} : payload.workspaceRoles,
        status: result.status,
        createdAt: idx >= 0 ? (prev[idx]?.createdAt ?? result.updatedAt) : result.updatedAt,
        updatedAt: result.updatedAt,
        updatedBy: session.uid,
      };
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = updatedItem;
        return copy;
      }
      return [...prev, updatedItem];
    });
  };

  const executeAction = async (
    payload: ManageWorkspaceMembershipCallablePayload,
    successText: string,
  ): Promise<boolean> => {
    setSubmitting(true);
    try {
      const result = await invokeManage(payload);
      applyLocalOptimisticUpdate(payload, result);
      setStatusMessage(successText);
      return true;
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.membersActionFallback));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleInviteSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!session || !canManageMembers) return;
    setErrorMessage(null);
    setStatusMessage(null);
    const normalizedEmail = inviteEmail.trim().toLowerCase();
    if (normalizedEmail.length === 0) {
      setErrorMessage('Please enter an email address to invite.');
      return;
    }
    if (session.email !== null && session.email.toLowerCase() === normalizedEmail) {
      setErrorMessage('Self-escalation or modifying your own membership is not permitted.');
      return;
    }
    const isOrgAdminInvite = inviteRole === 'ORG_ADMIN';
    const targetWsId = inviteWorkspaceId || session.activeWorkspaceId;
    const ok = await executeAction(
      {
        orgId: session.orgId,
        email: normalizedEmail,
        action: 'INVITE',
        orgRole: isOrgAdminInvite ? 'ORG_ADMIN' : null,
        workspaceRoles: isOrgAdminInvite ? {} : { [targetWsId]: inviteRole },
      },
      `Invited ${normalizedEmail} as ${inviteRole}.`,
    );
    if (ok) {
      setInviteEmail('');
      setInviteModalOpen(false);
    }
  };

  const handleRoleChange = async (
    member: MembershipDocument,
    nextRole: WorkspaceMembershipRole,
    targetWorkspaceIdOverride?: string,
  ): Promise<void> => {
    if (!session || !canManageMembers) return;
    setErrorMessage(null);
    setStatusMessage(null);
    if (isSelfMember(member, session)) {
      setErrorMessage('Self-escalation or modifying your own membership is not permitted.');
      return;
    }
    const nextWsRoles: Record<string, WorkspaceMembershipRole> = {};
    for (const [wsId, r] of Object.entries(member.workspaceRoles)) {
      if (r !== 'ORG_ADMIN') {
        nextWsRoles[wsId] = r;
      }
    }
    const targetWsId = targetWorkspaceIdOverride || editingWorkspaceId || session.activeWorkspaceId;
    if (nextRole !== 'ORG_ADMIN') {
      nextWsRoles[targetWsId] = nextRole;
    }
    const ok = await executeAction(
      {
        orgId: session.orgId,
        email: member.email,
        action: 'UPDATE_ROLES',
        orgRole: nextRole === 'ORG_ADMIN' ? 'ORG_ADMIN' : null,
        workspaceRoles: nextWsRoles,
      },
      `Updated role for ${member.email} to ${nextRole}.`,
    );
    if (ok) {
      setEditingMember(null);
    }
  };

  const handleDeactivate = async (member: MembershipDocument): Promise<void> => {
    if (!session || !canManageMembers) return;
    setErrorMessage(null);
    setStatusMessage(null);
    if (isSelfMember(member, session)) {
      setErrorMessage('You cannot deactivate your own membership.');
      return;
    }
    await executeAction(
      {
        orgId: session.orgId,
        email: member.email,
        action: 'REVOKE',
        orgRole: null,
        workspaceRoles: {},
      },
      `Deactivated membership for ${member.email}.`,
    );
  };

  return (
    <Card data-testid="members-management-card" className="h-full">
      {submitting && <LinearSpinner />}
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CardIcon>
            <IconBrandShield />
          </CardIcon>
          <CardTitle>Team Members</CardTitle>
          <Tag variant="outline" size="sm">
            {memberships.length}
          </Tag>
        </div>
        {canManageMembers ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={submitting}
              data-testid="open-invite-member-btn"
              onClick={() => {
                setInviteEmail('');
                setInviteRole('CAMPAIGN_EDITOR');
                setInviteWorkspaceId(session?.activeWorkspaceId ?? '');
                setErrorMessage(null);
                setInviteModalOpen(true);
              }}
            >
              <IconPlus />
              <span>Invite Member</span>
            </Button>
          </div>
        ) : (
          <span
            role="status"
            data-testid="members-readonly-notice"
            className="text-[11px] font-medium text-fg-muted"
          >
            {formatCapabilityRestrictionNotice({
              capability: 'membership:manage_callable',
              actionSummary: 'invite team members, change roles, or deactivate access',
              callerRole: session?.personaRole,
            })}
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <CardDescription>
          Manage organisation-wide admins and per-workspace team roles.
        </CardDescription>

        <ToastNotification message={statusMessage} onDismiss={() => setStatusMessage(null)} />
        {errorMessage !== null && !inviteModalOpen && editingMember === null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {errorMessage}
          </p>
        )}

        {loading ? (
          <div role="status" className="flex items-center gap-2 text-[12px] text-fg-secondary">
            <Spinner size={16} />
            <span>{ADMIN_STRINGS.loading.members}</span>
          </div>
        ) : memberships.length === 0 ? (
          <p className="text-[12px] text-fg-secondary">{ADMIN_STRINGS.empty.members}</p>
        ) : (
          <div className="overflow-x-auto rounded-control border border-subtle">
            <table className="w-full border-collapse text-left text-[14px]">
              <thead>
                <tr className="border-b border-border bg-surface-muted text-[12px] font-medium text-fg-secondary">
                  <th scope="col" className="px-3.5 py-2.5">
                    Email
                  </th>
                  <th scope="col" className="px-3.5 py-2.5">
                    Status
                  </th>
                  <th scope="col" className="px-3.5 py-2.5">
                    Role
                  </th>
                  {canManageMembers && (
                    <th scope="col" className="px-3.5 py-2.5 text-right">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-subtle">
                {memberships.map((member) => (
                  <MemberRow
                    key={member.membershipId}
                    member={member}
                    isSelf={isSelfMember(member, session)}
                    currentRole={resolveDisplayedMemberRole(
                      member,
                      session?.activeWorkspaceId ?? 'ws_default',
                    )}
                    canManageMembers={canManageMembers}
                    submitting={submitting}
                    availableWorkspaces={availableWorkspaces}
                    onStartEditRole={(m, current) => {
                      const targetWs = session?.activeWorkspaceId ?? 'ws_default';
                      setEditingMember(m);
                      setEditingWorkspaceId(targetWs);
                      setEditingRoleDraft(current);
                      setErrorMessage(null);
                    }}
                    onDeactivate={(m) => void handleDeactivate(m)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <InviteMemberModal
        open={inviteModalOpen}
        workspaceId={inviteWorkspaceId || session?.activeWorkspaceId || ''}
        availableWorkspaces={availableWorkspaces}
        onWorkspaceChange={setInviteWorkspaceId}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={() => {
          setInviteModalOpen(false);
          setErrorMessage(null);
        }}
        onSubmit={(e) => void handleInviteSubmit(e)}
      />

      <EditMemberRoleModal
        target={editingMember}
        targetWorkspaceId={editingWorkspaceId || session?.activeWorkspaceId || ''}
        availableWorkspaces={availableWorkspaces}
        onTargetWorkspaceChange={(nextWsId) => {
          setEditingWorkspaceId(nextWsId);
          if (editingMember) {
            setEditingRoleDraft(resolveDisplayedMemberRole(editingMember, nextWsId));
          }
        }}
        roleDraft={editingRoleDraft}
        setRoleDraft={setEditingRoleDraft}
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={() => {
          setEditingMember(null);
          setErrorMessage(null);
        }}
        onSave={(member, nextRole, wsId) => void handleRoleChange(member, nextRole, wsId)}
      />
    </Card>
  );
}
